import React, { useState, useMemo, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Popover, Input, Empty, Tooltip } from "antd";
import { SearchOutlined, DownOutlined, CloseCircleFilled } from "@ant-design/icons";
import * as FaIcons from "react-icons/fa";
import "./IconSelect.css";

// Extract valid FontAwesome icon keys once
const ALL_ICON_KEYS = Object.keys(FaIcons).filter(
  (key) => key.startsWith("Fa") && typeof FaIcons[key] === "function"
);

// Format CamelCase icon keys into clean readable words
// e.g. "FaHome" -> "Home", "FaChartLine" -> "Chart Line", "FaRegClock" -> "Clock"
const formatIconName = (key) => {
  if (!key) return "";
  const stripped = key.replace(/^Fa(Reg)?/, "");
  return stripped.replace(/([A-Z])/g, " $1").trim() || key;
};

const INITIAL_BATCH = 48;
const BATCH_STEP = 24;

export const IconSelect = ({
  value,
  onChange,
  placeholder = "Select Icon",
  columns = 4,
  disabled = false,
  allowClear = true,
  size = "large",
  className = "",
  style = {},
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [displayLimit, setDisplayLimit] = useState(INITIAL_BATCH);
  const searchInputRef = useRef(null);
  const gridContainerRef = useRef(null);

  // Focus search input when popover opens
  useEffect(() => {
    let timer;
    if (open) {
      timer = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 50);
    } else {
      setSearch("");
      setDisplayLimit(INITIAL_BATCH);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [open]);

  // Fast in-memory search over string keys (<1ms)
  const filteredIcons = useMemo(() => {
    const trimmed = search.trim().toLowerCase();
    if (!trimmed) {
      return ALL_ICON_KEYS;
    }
    return ALL_ICON_KEYS.filter((key) => {
      const formatted = formatIconName(key).toLowerCase();
      return key.toLowerCase().includes(trimmed) || formatted.includes(trimmed);
    });
  }, [search]);

  // Sliced items for DOM rendering to guarantee 60fps performance
  const visibleIcons = useMemo(() => {
    return filteredIcons.slice(0, displayLimit);
  }, [filteredIcons, displayLimit]);

  // Handle infinite scroll inside the grid
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 60) {
      if (displayLimit < filteredIcons.length) {
        setDisplayLimit((prev) => Math.min(prev + BATCH_STEP, filteredIcons.length));
      }
    }
  };

  const handleSelect = (iconKey) => {
    if (disabled) return;
    if (onChange) {
      onChange(iconKey);
    }
    setOpen(false);
  };

  const handleClear = (e) => {
    if (e) e.stopPropagation();
    if (disabled) return;
    if (onChange) {
      onChange(undefined);
    }
  };

  // Direct O(1) icon lookup for trigger display
  const SelectedIconComponent = value && FaIcons[value] ? FaIcons[value] : null;

  const content = (
    <div
      className={`icon-select-dropdown columns-${columns}`}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="icon-select-search-header">
        <Input
          ref={searchInputRef}
          prefix={<SearchOutlined style={{ color: "#8c8c8c" }} />}
          placeholder="Search system icons..."
          value={search}
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => {
            setSearch(e.target.value);
            setDisplayLimit(INITIAL_BATCH);
            if (gridContainerRef.current) {
              gridContainerRef.current.scrollTop = 0;
            }
          }}
          allowClear
          className="icon-select-search-input"
        />
      </div>

      <div
        ref={gridContainerRef}
        className={`icon-select-grid-container columns-${columns}`}
        onScroll={handleScroll}
      >
        {visibleIcons.length > 0 ? (
          visibleIcons.map((key) => {
            const IconComp = FaIcons[key];
            const isSelected = key === value;
            const cleanName = formatIconName(key);

            return (
              <Tooltip key={key} title={cleanName} mouseEnterDelay={0.5}>
                <div
                  className={`icon-grid-card ${isSelected ? "selected" : ""}`}
                  onClick={() => handleSelect(key)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="icon-grid-icon-wrapper">
                    {IconComp ? <IconComp /> : null}
                  </div>
                  <span className="icon-grid-card-label">{cleanName}</span>
                </div>
              </Tooltip>
            );
          })
        ) : (
          <div className="icon-select-empty" style={{ gridColumn: `span ${columns}` }}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No icons found" />
          </div>
        )}
      </div>

      <div className="icon-select-footer">
        <span>
          {filteredIcons.length > 0
            ? `Showing ${Math.min(visibleIcons.length, filteredIcons.length)} of ${filteredIcons.length} icons`
            : "0 icons"}
        </span>
        {displayLimit < filteredIcons.length && (
          <span
            className="icon-select-load-more"
            onClick={() =>
              setDisplayLimit((prev) => Math.min(prev + BATCH_STEP * 2, filteredIcons.length))
            }
          >
            Load More
          </span>
        )}
      </div>
    </div>
  );

  return (
    <Popover
      open={open && !disabled}
      onOpenChange={(nextOpen) => {
        if (!disabled) setOpen(nextOpen);
      }}
      trigger="click"
      placement="bottomLeft"
      content={content}
      overlayClassName="icon-select-popover"
      destroyTooltipOnHide={false}
      getPopupContainer={(triggerNode) =>
        triggerNode.closest(".modal-content") ||
        triggerNode.closest(".modal") ||
        triggerNode.parentNode ||
        document.body
      }
    >
      <div
        className={`icon-select-trigger ${size ? `icon-select-${size}` : ""} ${
          disabled ? "disabled" : ""
        } ${open ? "open" : ""} ${className}`}
        style={style}
        role="combobox"
        aria-expanded={open}
        tabIndex={disabled ? -1 : 0}
      >
        <div className="icon-select-trigger-content">
          {SelectedIconComponent ? (
            <>
              <span className="icon-select-trigger-icon">
                <SelectedIconComponent />
              </span>
              <span className="icon-select-trigger-text">{formatIconName(value)}</span>
            </>
          ) : (
            <span className="icon-select-placeholder">{placeholder}</span>
          )}
        </div>
        <div className="icon-select-trigger-actions">
          {allowClear && value && !disabled && (
            <span className="icon-select-clear-btn" onClick={handleClear} title="Clear icon">
              <CloseCircleFilled />
            </span>
          )}
          <DownOutlined className={`icon-select-arrow ${open ? "open" : ""}`} />
        </div>
      </div>
    </Popover>
  );
};

IconSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  columns: PropTypes.oneOf([4, 6]),
  disabled: PropTypes.bool,
  allowClear: PropTypes.bool,
  size: PropTypes.oneOf(["small", "middle", "large"]),
  className: PropTypes.string,
  style: PropTypes.object,
};

export default IconSelect;
